import { useMemo } from "react";

import useClinicOptionsForLock from "./use-clinic-options-for-lock.hook";
import useReportFilterLock from "./use-report-filter-lock.hook";
import useStaffOptionForLock from "./use-staff-option-for-lock.hook";

export type LockedFilterOption = { label: string; value: string };

export type LockedFilterValues = {
  /** filterKey -> the option objects to seed, for the picker and the pill label. */
  options: Record<string, LockedFilterOption[]>;
  /**
   * filterKey -> the scalar the filter's own edit path would have sent.
   *
   * Reports that pass their accumulated filter state straight to the query (staff metrics and
   * friends) serialize this with `URLSearchParams`, so an option object here becomes
   * `provider=[object Object]` and the report silently returns everything. Every edit path sends
   * either `values.join(",")` or a bare `option.value` — identical for a single option — so the
   * joined string is correct for both.
   */
  params: Record<string, string>;
  /** Keys under a permission lock. These render as a locked pill and cannot be cleared. */
  lockedKeys: Set<string>;
  /** Keys with nothing left to choose: self-only staff, or a clinic scope of exactly one. */
  pinnedKeys: Set<string>;
};

const EMPTY: LockedFilterValues = {
  options: {},
  params: {},
  lockedKeys: new Set(),
  pinnedKeys: new Set()
};

/**
 * The filter values pinned by the current user's report permissions, in every shape the filter bar
 * needs them: option objects for the picker and the pill, and a scalar for the request params.
 *
 * Single source for both consumers — `useCustomFilters` seeds these into the report's filter state,
 * and `CustomFiltersList` renders them as locked pills. Returns empty for a user who is
 * unconfigured, in simple mode, or with the flag off.
 */
export default function useLockedFilterValues(): LockedFilterValues {
  const filterLock = useReportFilterLock();
  const lockedStaffOption = useStaffOptionForLock(filterLock.lockedStaffId);
  const scopedClinicOptions = useClinicOptionsForLock(
    filterLock.allowedClinicIds
  );

  const {
    lockedStaffFilter,
    lockedStaffId,
    scopedClinicFilter,
    allowedClinicIds
  } = filterLock;

  return useMemo(() => {
    // `params` and the key sets come straight from the lock's ids, so the request is scoped from
    // the first render. `options` needs the labels and may lag one fetch behind — a report that
    // auto-runs on mount must not send an unscoped request while the clinic names load.
    const options: Record<string, LockedFilterOption[]> = {};
    const params: Record<string, string> = {};
    const lockedKeys = new Set<string>();
    const pinnedKeys = new Set<string>();

    if (lockedStaffFilter && lockedStaffId) {
      params[lockedStaffFilter] = lockedStaffId;
      lockedKeys.add(lockedStaffFilter);
      // Self-only pins the filter to one person; there is nothing to pick.
      pinnedKeys.add(lockedStaffFilter);
      if (lockedStaffOption) {
        options[lockedStaffFilter] = [lockedStaffOption];
      }
    }

    if (scopedClinicFilter && allowedClinicIds?.length) {
      params[scopedClinicFilter] = allowedClinicIds.join(",");
      lockedKeys.add(scopedClinicFilter);
      // With several allowed clinics the user still chooses among them, so only a scope of one pins.
      if (allowedClinicIds.length === 1) {
        pinnedKeys.add(scopedClinicFilter);
      }
      if (scopedClinicOptions?.length) {
        options[scopedClinicFilter] = scopedClinicOptions.map(
          ({ label, value }) => ({ label, value })
        );
      }
    }

    if (!lockedKeys.size) return EMPTY;

    return { options, params, lockedKeys, pinnedKeys };
  }, [
    lockedStaffFilter,
    lockedStaffId,
    scopedClinicFilter,
    allowedClinicIds,
    lockedStaffOption,
    scopedClinicOptions
  ]);
}
